import { ADMIN_BUILD_INFO } from "../../../.generated/build-info";
import type { ReviewSelections } from "./reviewStateModel";

const PREFIX = "ember:photo-review-draft:v1";
const ADMIN_TOKEN_KEY = "ember_admin_token";
const IMPERSONATOR_BACKUP_KEY = "ember_admin_token_impersonator_backup";
const TTL_MS = 24 * 60 * 60 * 1000;
const DEBOUNCE_MS = 300;

export type ReviewDraftIdentity = {
  accountKey: string;
  impersonationKey: string;
  taskId: number;
  round: number;
  version: number;
};

export type ReviewDraftPayload = ReviewSelections & {
  taskId: number;
  round: number;
  version: number;
  itemIds: number[];
  updatedAt: number;
  buildId: string;
};

export type LoadedReviewDraft =
  | { status: "missing" }
  | { status: "valid"; payload: ReviewDraftPayload }
  | { status: "expired" | "invalid" };

type StorageLike = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem" | "key" | "length"
>;

function storage(): StorageLike | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safePart(value: string): string {
  return encodeURIComponent(value.trim() || "unknown");
}

function jwtSubject(token: string): string | null {
  const payload = token.split(".")[1];
  if (!payload || typeof atob !== "function") return null;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const decoded = JSON.parse(atob(padded)) as Record<string, unknown>;
    for (const value of [decoded.employee_id, decoded.sub, decoded.actor_id]) {
      if (typeof value === "number" || typeof value === "string") {
        const normalized = String(value).trim();
        if (normalized) return normalized;
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function currentReviewDraftIdentity(input: {
  effectiveEmployeeId: number | null;
  taskId: number;
  round: number;
  version: number;
}): ReviewDraftIdentity {
  const target = storage();
  const effectiveToken = target?.getItem(ADMIN_TOKEN_KEY) ?? "";
  const backupToken = target?.getItem(IMPERSONATOR_BACKUP_KEY) ?? "";
  const effectiveKey =
    jwtSubject(effectiveToken) ??
    String(input.effectiveEmployeeId ?? "unknown");
  return {
    accountKey: jwtSubject(backupToken) ?? effectiveKey,
    impersonationKey: backupToken ? effectiveKey : "none",
    taskId: input.taskId,
    round: input.round,
    version: input.version,
  };
}

export function reviewDraftKey(identity: ReviewDraftIdentity): string {
  return [
    PREFIX,
    safePart(identity.accountKey),
    safePart(identity.impersonationKey),
    identity.taskId,
    identity.round,
    identity.version,
  ].join("::");
}

function reviewDraftTaskPrefix(identity: ReviewDraftIdentity): string {
  return [
    PREFIX,
    safePart(identity.accountKey),
    safePart(identity.impersonationKey),
    identity.taskId,
  ].join("::");
}

export function buildReviewDraft(
  identity: ReviewDraftIdentity,
  selections: ReviewSelections,
  itemIds: number[],
  now = Date.now(),
): ReviewDraftPayload {
  return {
    taskId: identity.taskId,
    round: identity.round,
    version: identity.version,
    verdicts: selections.verdicts,
    reasons: selections.reasons,
    itemIds,
    updatedAt: now,
    buildId: ADMIN_BUILD_INFO.buildId,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parsePayload(raw: string): ReviewDraftPayload | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value)) return null;
    if (
      typeof value.taskId !== "number" ||
      typeof value.round !== "number" ||
      typeof value.version !== "number" ||
      typeof value.updatedAt !== "number" ||
      typeof value.buildId !== "string" ||
      !Array.isArray(value.itemIds) ||
      !isRecord(value.verdicts) ||
      !isRecord(value.reasons)
    ) {
      return null;
    }
    const verdicts: Record<number, "passed" | "returned"> = {};
    for (const [rawId, verdict] of Object.entries(value.verdicts)) {
      if (verdict === "passed" || verdict === "returned") {
        verdicts[Number(rawId)] = verdict;
      }
    }
    const reasons: Record<number, string> = {};
    for (const [rawId, reason] of Object.entries(value.reasons)) {
      if (typeof reason === "string") reasons[Number(rawId)] = reason;
    }
    const itemIds = value.itemIds.filter(
      (itemId): itemId is number => typeof itemId === "number",
    );
    return {
      taskId: value.taskId,
      round: value.round,
      version: value.version,
      updatedAt: value.updatedAt,
      buildId: value.buildId,
      itemIds,
      verdicts,
      reasons,
    };
  } catch {
    return null;
  }
}

export function loadReviewDraft(
  identity: ReviewDraftIdentity,
  now = Date.now(),
): LoadedReviewDraft {
  const target = storage();
  if (!target) return { status: "missing" };
  const key = reviewDraftKey(identity);
  const raw = target.getItem(key);
  if (!raw) return { status: "missing" };
  const payload = parsePayload(raw);
  if (!payload) {
    target.removeItem(key);
    return { status: "invalid" };
  }
  if (now - payload.updatedAt > TTL_MS) {
    target.removeItem(key);
    return { status: "expired" };
  }
  return { status: "valid", payload };
}

export function clearReviewDraft(identity: ReviewDraftIdentity): void {
  storage()?.removeItem(reviewDraftKey(identity));
}

export function hasOtherReviewDraftForTask(
  identity: ReviewDraftIdentity,
): boolean {
  const target = storage();
  if (!target) return false;
  const prefix = `${reviewDraftTaskPrefix(identity)}::`;
  const exact = reviewDraftKey(identity);
  for (let index = 0; index < target.length; index += 1) {
    const key = target.key(index);
    if (key?.startsWith(prefix) && key !== exact) return true;
  }
  return false;
}

export function clearReviewDraftsForTask(identity: ReviewDraftIdentity): void {
  const target = storage();
  if (!target) return;
  const prefix = `${reviewDraftTaskPrefix(identity)}::`;
  const keys: string[] = [];
  for (let index = 0; index < target.length; index += 1) {
    const key = target.key(index);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  for (const key of keys) target.removeItem(key);
}

export function clearAllReviewDrafts(): void {
  const target = storage();
  if (!target) return;
  const keys: string[] = [];
  for (let index = 0; index < target.length; index += 1) {
    const key = target.key(index);
    if (key?.startsWith(`${PREFIX}::`)) keys.push(key);
  }
  for (const key of keys) target.removeItem(key);
}

type ActiveDraftController = {
  hasDraft: () => boolean;
  flushAndVerify: () => boolean;
};

const activeControllers = new Set<ActiveDraftController>();

export function registerActiveReviewDraft(
  controller: ActiveDraftController,
): () => void {
  activeControllers.add(controller);
  return () => activeControllers.delete(controller);
}

export function prepareReviewDraftsForReload(): {
  hasDraft: boolean;
  verified: boolean;
} {
  let hasDraft = false;
  let verified = true;
  for (const controller of activeControllers) {
    if (!controller.hasDraft()) continue;
    hasDraft = true;
    verified = controller.flushAndVerify() && verified;
  }
  return { hasDraft, verified };
}

export class ReviewDraftController {
  private timer: number | null = null;
  private pending: ReviewDraftPayload | null = null;

  constructor(private readonly identity: ReviewDraftIdentity) {}

  schedule(payload: ReviewDraftPayload): void {
    this.pending = payload;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.flushAndVerify();
    }, DEBOUNCE_MS);
  }

  hasDraft(): boolean {
    return (
      this.pending !== null || loadReviewDraft(this.identity).status === "valid"
    );
  }

  flushAndVerify(): boolean {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.pending) return loadReviewDraft(this.identity).status === "valid";
    const target = storage();
    if (!target) return false;
    const key = reviewDraftKey(this.identity);
    const raw = JSON.stringify(this.pending);
    try {
      target.setItem(key, raw);
      const verified = target.getItem(key) === raw;
      if (verified) this.pending = null;
      return verified;
    } catch {
      return false;
    }
  }

  clear(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.pending = null;
    clearReviewDraft(this.identity);
  }

  dispose(): void {
    this.flushAndVerify();
  }
}

export const REVIEW_DRAFT_TTL_MS = TTL_MS;
